import { useState } from 'react';
import { toast } from 'react-toastify';
import type { ExpertRequestView, ExpertSelfView } from '@dunea/shared';
import { apiGetChats } from '~/lib/api/chats';
import {
  apiActOnExpertRequest,
  apiCreateHandoff,
  apiGetExpertSelf,
  apiListExpertInbox,
  apiSaveExpertSelf,
  type ExpertRequestAction,
} from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  CopyField,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Section,
  Select,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { moneyLabel } from '~/components/workbench/tools/marketplace/community-model';
import {
  apiErrorMessage,
  emptyService,
  EXPERT_CURRENCIES,
  expertDraftErrors,
  expertDraftFrom,
  expertPayload,
  expertStatusMeta,
  requestActions,
  requestStatusMeta,
  type ExpertDraft,
  type ServiceDraft,
} from '~/components/marketplace/developer-model';

/** Experts directory: your profile, the requests clients send you, and site hand-offs. */
export function ExpertSection() {
  const self = useResource(() => apiGetExpertSelf(), 'expert-self');
  const [editing, setEditing] = useState(false);
  const status = self.data ? expertStatusMeta(self.data.status) : null;

  return (
    <Section
      title="Expert profile"
      icon="i-ph:briefcase"
      aside={status && <Pill tone={status.tone} label={status.label} />}
    >
      {self.error ? (
        <LoadError message="Could not load your expert profile." onRetry={() => void self.reload()} />
      ) : self.loading && !self.data ? (
        <SkeletonCard lines={2} />
      ) : editing || !self.data ? (
        !editing && !self.data ? (
          <div className={classNames(CARD, 'flex flex-wrap items-center justify-between gap-3 p-4')}>
            <p className="max-w-md text-[12px] leading-relaxed text-secondary">
              Take client work through the Experts directory. Dunea reviews profiles before they're listed.
            </p>
            <button type="button" onClick={() => setEditing(true)} className={BTN_SECONDARY}>
              Create a profile
            </button>
          </div>
        ) : (
          <ExpertEditor
            self={self.data}
            onCancel={() => setEditing(false)}
            onSaved={(next) => {
              self.setData(next);
              setEditing(false);
            }}
          />
        )
      ) : (
        <div className="space-y-4">
          <div className={classNames(CARD, 'space-y-2 p-4')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-primary">{self.data.headline}</div>
                <div className="mt-0.5 text-[11px] text-muted">
                  {self.data.services.length} {self.data.services.length === 1 ? 'service' : 'services'} ·{' '}
                  {self.data.acceptingWork ? 'Taking work' : 'Not taking work'} · {self.data.completedCount} completed
                </div>
              </div>
              <button type="button" onClick={() => setEditing(true)} className={BTN_GHOST}>
                Edit
              </button>
            </div>
            {self.data.status === 'pending' && (
              <p className="text-[11.5px] text-warning">Dunea is reviewing your profile. It's listed once approved.</p>
            )}
            {self.data.status === 'rejected' && (
              <p className="text-[11.5px] text-danger">Your profile wasn't approved. Edit it and save to resubmit.</p>
            )}
          </div>
          <RequestsInbox />
          <HandoffCard />
        </div>
      )}
    </Section>
  );
}

function ExpertEditor({
  self,
  onSaved,
  onCancel,
}: {
  self: ExpertSelfView | null;
  onSaved: (next: ExpertSelfView) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ExpertDraft>(() => expertDraftFrom(self));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = expertDraftErrors(draft);
  const set = (patch: Partial<ExpertDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const setService = (i: number, patch: Partial<ServiceDraft>) =>
    set({ services: draft.services.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  const save = async () => {
    setTried(true);

    if (errors.length > 0) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const next = await apiSaveExpertSelf(expertPayload(draft));
      toast.success(next.status === 'pending' ? 'Profile saved. Dunea will review it.' : 'Profile saved.');
      onSaved(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your profile.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      className={classNames(CARD, 'space-y-3 p-4')}
    >
      <Field label="Headline">
        <input
          value={draft.headline}
          onChange={(e) => set({ headline: e.target.value })}
          maxLength={120}
          placeholder="Shop setups for UK food brands"
          className="ui-input !py-2"
        />
      </Field>
      <Field label="About">
        <textarea
          value={draft.about}
          onChange={(e) => set({ about: e.target.value })}
          rows={4}
          maxLength={2000}
          className="ui-input resize-y"
        />
      </Field>

      <div className="space-y-2">
        <h4 className={CAPTION}>Services</h4>
        {draft.services.map((s, i) => (
          <div key={i} className="space-y-2 rounded-lg border border-hairline bg-sunken p-3">
            <div className="flex gap-2">
              <input
                value={s.title}
                onChange={(e) => setService(i, { title: e.target.value })}
                placeholder="Title"
                aria-label={`Service ${i + 1} title`}
                className="ui-input min-w-0 flex-1 !py-1.5"
              />
              {draft.services.length > 1 && (
                <button
                  type="button"
                  onClick={() => set({ services: draft.services.filter((_, j) => j !== i) })}
                  className="ui-btn-icon"
                  aria-label={`Remove service ${i + 1}`}
                >
                  <div className="i-ph:x text-[13px]" />
                </button>
              )}
            </div>
            <textarea
              value={s.description}
              onChange={(e) => setService(i, { description: e.target.value })}
              rows={2}
              placeholder="What's included"
              aria-label={`Service ${i + 1} description`}
              className="ui-input resize-y"
            />
            <div className="grid grid-cols-[1fr_90px_1fr] gap-2">
              <input
                value={s.price}
                onChange={(e) => setService(i, { price: e.target.value })}
                inputMode="decimal"
                placeholder="From price"
                aria-label={`Service ${i + 1} starting price`}
                className="ui-input !py-1.5"
              />
              <Select
                value={s.currency}
                onChange={(v) => setService(i, { currency: v as ServiceDraft['currency'] })}
                label="Currency"
              >
                {EXPERT_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
              <input
                value={s.deliveryDays}
                onChange={(e) => setService(i, { deliveryDays: e.target.value })}
                inputMode="numeric"
                placeholder="Days"
                aria-label={`Service ${i + 1} delivery days`}
                className="ui-input !py-1.5"
              />
            </div>
          </div>
        ))}
        {draft.services.length < 8 && (
          <button
            type="button"
            onClick={() => set({ services: [...draft.services, emptyService()] })}
            className={BTN_GHOST}
          >
            <div className="i-ph:plus text-[13px]" />
            Add a service
          </button>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Countries you work in" hint="Two-letter codes, like US, GB, IN">
          <input
            value={draft.countries}
            onChange={(e) => set({ countries: e.target.value })}
            className="ui-input !py-2"
          />
        </Field>
        <Field label="Languages" hint="Comma separated">
          <input
            value={draft.languages}
            onChange={(e) => set({ languages: e.target.value })}
            className="ui-input !py-2"
          />
        </Field>
      </div>
      <Field label="Portfolio links" hint="One https link per line">
        <textarea
          value={draft.portfolio}
          onChange={(e) => set({ portfolio: e.target.value })}
          rows={3}
          className="ui-input-mono resize-y !text-[11.5px]"
        />
      </Field>
      <Switch checked={draft.acceptingWork} onChange={(v) => set({ acceptingWork: v })} label="Taking new work" />

      {tried && errors.length > 0 && (
        <ul className="space-y-0.5 rounded-lg border border-danger/30 bg-danger/[0.05] p-3" role="alert">
          {errors.map((e) => (
            <li key={e} className="text-[11.5px] text-danger/90">
              {e}
            </li>
          ))}
        </ul>
      )}
      {error && <ErrorBanner message={error} />}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          Save profile
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const INBOX_FILTERS = [
  ['', 'All'],
  ['new', 'New'],
  ['accepted', 'Accepted'],
  ['completed', 'Completed'],
] as const;

const ACTION_LABEL: Record<ExpertRequestAction, string> = {
  accept: 'Accept',
  decline: 'Decline',
  complete: 'Mark complete',
};

function RequestsInbox() {
  const [filter, setFilter] = useState<'' | ExpertRequestView['status']>('');
  const inbox = useResource(() => apiListExpertInbox(filter || undefined), `inbox:${filter}`);
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (r: ExpertRequestView, action: ExpertRequestAction) => {
    setBusy(`${r.id}:${action}`);

    try {
      const next = await apiActOnExpertRequest(r.id, action);
      inbox.setData((prev) => (prev ?? []).map((x) => (x.id === next.id ? next : x)));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update this request.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className={CAPTION}>Requests</h4>
        <Select
          value={filter}
          onChange={(v) => setFilter(v as typeof filter)}
          label="Request status"
          className="w-[130px]"
        >
          {INBOX_FILTERS.map(([v, text]) => (
            <option key={v} value={v}>
              {text}
            </option>
          ))}
        </Select>
      </div>
      {inbox.error ? (
        <LoadError message="Could not load requests." onRetry={() => void inbox.reload()} />
      ) : !inbox.data ? (
        <SkeletonCard />
      ) : inbox.data.length === 0 ? (
        <p className="text-[12px] text-muted">No requests{filter ? ' with this status' : ' yet'}.</p>
      ) : (
        <ul className="space-y-2">
          {inbox.data.map((r) => {
            const meta = requestStatusMeta(r.status);

            return (
              <li key={r.id} className={classNames(CARD, 'space-y-2 p-3.5')}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[12.5px] text-primary">
                    {r.requester.name}
                    {r.requester.email && <span className="text-muted"> · {r.requester.email}</span>}
                  </span>
                  <Pill tone={meta.tone} label={meta.label} />
                </div>
                <div className="flex flex-wrap gap-x-2.5 text-[11px] text-muted">
                  <span>{formatDate(r.createdAt)}</span>
                  {r.service && <span>{r.service}</span>}
                  {r.budgetCents !== null && <span>Budget {moneyLabel(r.budgetCents, 'USD')}</span>}
                  {r.appId && <span>About an existing app</span>}
                </div>
                <p className="whitespace-pre-line text-[12px] leading-relaxed text-secondary">{r.message}</p>
                {requestActions(r.status).length > 0 && (
                  <div className="flex gap-1.5">
                    {requestActions(r.status).map((action) => (
                      <button
                        key={action}
                        type="button"
                        onClick={() => void act(r, action)}
                        disabled={busy !== null}
                        className={action === 'decline' ? BTN_GHOST : BTN_SECONDARY}
                      >
                        {busy === `${r.id}:${action}` && <Spinner />}
                        {ACTION_LABEL[action]}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Hand a site you built to a client: they claim it from a link, and it stays attributed to you. */
function HandoffCard() {
  const chats = useResource(() => apiGetChats(), 'my-chats');
  const [chatId, setChatId] = useState('');
  const [busy, setBusy] = useState(false);
  const [claimUrl, setClaimUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!chatId) {
      return;
    }

    setBusy(true);
    setError(null);
    setClaimUrl(null);

    try {
      const { claimUrl: url } = await apiCreateHandoff(chatId);
      setClaimUrl(url);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the hand-off link.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classNames(CARD, 'space-y-2.5 p-4')}>
      <h4 className={CAPTION}>Hand off a site</h4>
      <p className="text-[12px] leading-relaxed text-secondary">
        Send your client a link. When they claim it, the site moves to their account.
      </p>
      {chats.error ? (
        <LoadError message="Could not load your sites." onRetry={() => void chats.reload()} />
      ) : !chats.data ? (
        <SkeletonCard lines={1} />
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            disabled={busy}
            aria-label="Site"
            className="ui-input !py-2"
          >
            <option value="">Pick a site</option>
            {chats.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.description || 'Untitled app'}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void create()}
            disabled={busy || !chatId}
            className={classNames(BTN_SECONDARY, 'shrink-0')}
          >
            {busy && <Spinner />}
            Create link
          </button>
        </div>
      )}
      {error && <ErrorBanner message={error} />}
      {claimUrl && <CopyField label="Claim link" value={claimUrl} />}
    </div>
  );
}
